// One value out of a result set. SQLite hands back the five types it has; pglite parses Postgres text into whatever
// JavaScript it decided the column meant, which is anything at all — dates, arrays, objects and buffers included.
export type Cell = unknown;

// A cell too long to be a cell: the grid truncates rather than the browser laying out a megabyte of it in a row.
const MAX_CELL = 2000;

// The grid is not virtualised, so past this it is a page that takes a second to lay out and stutters as it scrolls.
// Only what is drawn is capped — the count above the table is still the count the database answered with.
export const MAX_ROWS = 1000;

// What a value is written as in the grid. NULL is the one answer that is not the value's own text, which is why the
// grid asks `isNull` separately and paints it dimmed — an empty string and a NULL read identically otherwise.
export function writeCell(value: Cell): string {
  if (value === null || value === undefined) return "NULL";
  if (typeof value === "string") return clip(value);
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date) return value.toISOString();
  if (value instanceof Uint8Array) return writeBytes(value);
  if (value instanceof ArrayBuffer) return writeBytes(new Uint8Array(value));
  if (ArrayBuffer.isView(value)) return writeBytes(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));

  try {
    return clip(JSON.stringify(value) ?? String(value));
  } catch {
    return String(value);
  }
}

export function isNull(value: Cell): boolean {
  return value === null || value === undefined;
}

// A blob in the hex literal both dialects would take back, so what is on screen is something that can be pasted.
function writeBytes(bytes: Uint8Array): string {
  const shown = bytes.subarray(0, MAX_BLOB);
  let hex = "";
  for (const byte of shown) hex += byte.toString(16).padStart(2, "0");
  return bytes.length > MAX_BLOB ? `x'${hex}…' (${bytes.length} bytes)` : `x'${hex}'`;
}

function clip(text: string): string {
  return text.length > MAX_CELL ? `${text.slice(0, MAX_CELL)}…` : text;
}

const MAX_BLOB = 64;
